import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import {
  ShoppingCart, User, LogOut, BookOpen, Settings, Menu, Heart, Home,
  Library, Info, ChevronDown, Plus, Shield, Eye, EyeOff, IndianRupee,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";

const BROWSE_CATEGORIES = [
  'Adventure', 'Fantasy', 'Biography', 'Sci-Fi', 'Fiction',
  'Psychology', 'Self-Development', 'Leadership', 'Spiritual', 'Academic', 'Others'
];
const BROWSE_AGES = ['4-12', '13-21', '22-30', '30+'];
const NAV_PILL_CLASS =
  'px-3 py-1.5 rounded-full text-sm font-medium transition-colors hover:opacity-90 inline-flex items-center gap-1';
const NAV_PILL_STYLE = { backgroundColor: 'var(--primary)', color: 'white' };
const HIDE_BAL_KEY = 'mbr_hide_deposit_balance';
import { isProsumer, isPlatformAdmin, isBookOwner, isEmployee, roleLabel } from '../lib/roles';

const Navbar = () => {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [depositBalance, setDepositBalance] = useState(null);
  const [hideBalance, setHideBalance] = useState(() => {
    try {
      return localStorage.getItem(HIDE_BAL_KEY) === '1';
    } catch {
      return false;
    }
  });

  const fetchCartCount = async () => {
    if (!token) {
      setCartCount(0);
      return;
    }
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : [];
      const count = items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
      setCartCount(Number.isFinite(count) ? count : 0);
    } catch (e) {
      setCartCount(0);
    }
  };

  const fetchDepositBalance = async () => {
    if (!token) {
      setDepositBalance(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/user/deposit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDepositBalance(Number(res.data?.available_balance ?? res.data?.balance) || 0);
    } catch {
      setDepositBalance(0);
    }
  };

  const toggleHideBalance = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHideBalance((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(HIDE_BAL_KEY, next ? '1' : '0');
      } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    fetchCartCount();
    fetchDepositBalance();
    const onCartUpdated = () => fetchCartCount();
    const onDepositUpdated = () => fetchDepositBalance();
    window.addEventListener('cart-updated', onCartUpdated);
    window.addEventListener('deposit-updated', onDepositUpdated);
    window.addEventListener('focus', onDepositUpdated);
    return () => {
      window.removeEventListener('cart-updated', onCartUpdated);
      window.removeEventListener('deposit-updated', onDepositUpdated);
      window.removeEventListener('focus', onDepositUpdated);
    };
  }, [user, token]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const goBrowseBooks = (params = {}) => {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.q) query.set('q', params.q);
    const qs = query.toString();
    navigate(`/${qs ? `?${qs}` : ''}#browse-books`);
    setTimeout(() => {
      document.getElementById('browse-books')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const balanceLabel = hideBalance
    ? '₹••••'
    : `₹${Math.round(depositBalance ?? 0).toLocaleString('en-IN')}`;

  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')} data-testid="navbar-logo-wrap">
            <BookOpen className="h-8 w-8" style={{color: 'var(--primary)'}} />
            <h1 className="text-2xl font-bold" style={{color: 'var(--primary)'}} data-testid="navbar-logo">
              MyBook.Rent
            </h1>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={() => navigate('/')}
              className={NAV_PILL_CLASS}
              style={NAV_PILL_STYLE}
              data-testid="navbar-home"
            >
              <Home className="h-4 w-4 sm:hidden" />
              <span className="hidden sm:inline">Home</span>
            </button>

            <button
              onClick={() => navigate('/about')}
              className={NAV_PILL_CLASS}
              style={NAV_PILL_STYLE}
              data-testid="navbar-about"
            >
              <Info className="h-4 w-4 sm:hidden" />
              <span className="hidden sm:inline">About Us</span>
            </button>

            <button
              onClick={() => navigate('/scholarship/eligibility')}
              className={NAV_PILL_CLASS}
              style={NAV_PILL_STYLE}
              data-testid="navbar-scholarships"
            >
              <span className="hidden sm:inline">Scholarships</span>
              <span className="sm:hidden">Aid</span>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={NAV_PILL_CLASS}
                  style={NAV_PILL_STYLE}
                  data-testid="navbar-browse"
                >
                  <BookOpen className="h-4 w-4 sm:hidden" />
                  <span className="hidden sm:inline">Book Browser</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56" data-testid="browse-dropdown">
                <DropdownMenuItem onClick={() => goBrowseBooks()} data-testid="browse-all-books">
                  All books
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="browse-by-category">
                    Books by Category
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-52">
                    {BROWSE_CATEGORIES.map((cat) => (
                      <DropdownMenuItem
                        key={cat}
                        onClick={() => goBrowseBooks({ category: cat })}
                        data-testid={`browse-cat-${cat.toLowerCase()}`}
                      >
                        {cat}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="browse-by-age">
                    Books by Age
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-40">
                    {BROWSE_AGES.map((age) => (
                      <DropdownMenuItem
                        key={age}
                        onClick={() => goBrowseBooks({ q: `age:${age}` })}
                        data-testid={`browse-age-${age}`}
                      >
                        {age} years
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <>
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/cart')}
                  className="flex items-center space-x-1 relative"
                  data-testid="navbar-cart"
                >
                  <ShoppingCart className="h-4 w-4" />
                  <span className="hidden sm:inline">Cart</span>
                  <span
                    className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold"
                    data-testid="navbar-cart-count"
                  >
                    {cartCount || 0}
                  </span>
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center space-x-2 h-auto p-2">
                      {user.profile_image ? (
                        <img 
                          src={user.profile_image} 
                          alt={user.name} 
                          className="w-8 h-8 rounded-full object-cover border-2"
                          style={{borderColor: 'var(--primary)'}}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: 'var(--primary)'}}>
                          <User className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <span className="hidden md:block text-sm font-medium">{user.name}</span>
                      <Menu className="h-4 w-4 text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-col">
                          <span>My Account</span>
                          <span className="text-xs font-normal text-gray-500" data-testid="navbar-role-label">
                            {roleLabel(user.role)}
                          </span>
                        </div>
                        <div
                          className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"
                          data-testid="navbar-deposit-balance"
                        >
                          <div className="min-w-0">
                            <p className="text-[10px] font-normal uppercase tracking-wide text-gray-500">Deposit</p>
                            <p
                              className="text-sm font-semibold tabular-nums"
                              style={{ color: 'var(--primary)' }}
                              data-testid="navbar-deposit-amount"
                            >
                              {depositBalance === null ? '…' : balanceLabel}
                            </p>
                          </div>
                          <button
                            type="button"
                            onPointerDown={(e) => e.preventDefault()}
                            onClick={toggleHideBalance}
                            className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-white shrink-0"
                            aria-label={hideBalance ? 'Show deposit balance' : 'Hide deposit balance'}
                            data-testid="navbar-deposit-toggle"
                          >
                            {hideBalance ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem onClick={() => navigate('/transactions')} data-testid="navbar-transactions">
                      <IndianRupee className="mr-2 h-4 w-4" />
                      <span>Transactions <span className="text-xs text-gray-400">money in and out</span></span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/notifications')} data-testid="navbar-notifications">
                      <span>Notifications <span className="text-xs text-gray-400">WhatsApp and site alerts</span></span>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() => navigate('/edit-profile?addDeposit=1')}
                      data-testid="navbar-add-security-deposit"
                    >
                      <Shield className="mr-2 h-4 w-4" />
                      <span>Add security deposit</span>
                    </DropdownMenuItem>

                    {isBookOwner(user.role) && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/admin?add=1')} data-testid="navbar-add-book">
                          <Plus className="mr-2 h-4 w-4" />
                          <span>Add a Book</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/my-copies')} data-testid="navbar-my-copies">
                          <Library className="mr-2 h-4 w-4" />
                          <span>My Copies <span className="text-xs text-gray-400">books you list</span></span>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    <DropdownMenuItem onClick={() => navigate('/edit-profile')} data-testid="navbar-edit-profile">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Edit Profile</span>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem onClick={() => navigate('/customer-dashboard')} data-testid="navbar-my-rentals">
                      <BookOpen className="mr-2 h-4 w-4" />
                      <span>My Rentals <span className="text-xs text-gray-400">books you borrowed</span></span>
                    </DropdownMenuItem>
                    
                    {isProsumer(user.role) && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="navbar-admin">
                        <BookOpen className="mr-2 h-4 w-4" />
                        <span>Prosumer Dashboard</span>
                      </DropdownMenuItem>
                    )}

                    {isEmployee(user.role) && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="navbar-employee-admin">
                          <BookOpen className="mr-2 h-4 w-4" />
                          <span>Employee Dashboard</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/operations')} data-testid="navbar-operations">
                          <Library className="mr-2 h-4 w-4" />
                          <span>Operations — All Orders</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {isPlatformAdmin(user.role) && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/super-admin')} data-testid="navbar-super-admin">
                          <BookOpen className="mr-2 h-4 w-4" />
                          <span>{user.role === 'founder' ? 'Founder Dashboard' : 'Executive Leader Dashboard'}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/operations')} data-testid="navbar-operations-admin">
                          <Library className="mr-2 h-4 w-4" />
                          <span>Operations — All Orders</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem onClick={() => navigate('/scholarship/status')} data-testid="navbar-scholarship-status">
                      <span>Track scholarship</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/scholarship/utilization')} data-testid="navbar-scholarship-utilization">
                      <span>Scholarship utilization</span>
                    </DropdownMenuItem>
                    {(isEmployee(user.role) || isPlatformAdmin(user.role)) && (
                      <DropdownMenuItem onClick={() => navigate('/scholarship/admin')} data-testid="navbar-scholarship-admin">
                        <span>Scholarship applications</span>
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuItem onClick={() => navigate('/reading-preferences')} data-testid="navbar-preferences">
                      <Heart className="mr-2 h-4 w-4" />
                      <span>Reading Preferences</span>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="navbar-logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Logout</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate('/login')} data-testid="navbar-login">
                  Login
                </Button>
                <Button 
                  size="sm"
                  onClick={() => navigate('/register')}
                  style={{backgroundColor: 'var(--primary)', color: 'white'}}
                  className="hover:opacity-90"
                  data-testid="navbar-signup"
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
